import {memo} from 'react';
import propsAreEqual from 'react-fast-compare';
import {generateText} from '@tiptap/react';
import {colors} from 'patient-ping-remedy/packages/theme';
import ToolTip from 'patient-ping-remedy/packages/tool-tip';
import {SmallTypography} from 'patient-ping-remedy/packages/typography';
import {MessageThreadSummary} from '@/api/dto/message';
import {useMessageStore} from '@/store/message_store';
import {extensions} from '@common/messages/MessageEditor';
import {useMessageThreadItem} from '@/hooks/useMessageThreadItem';
import {useTimeSince} from '@/hooks/useTimeSince';
import WidgetElementViewed from '@common/widgets/WidgetElementViewed';
import Badge from 'patient-ping-remedy/packages/badge';
import {Wrapper, Main, Title, About, Content, TimeSince, Message, Viewed, StyledBadge}
  from '@/components/pages/messages/MessageThreadItem.styles';
import {SearchParam} from '@/helpers/search_params';

type Props = {
  messageThreadSummary: MessageThreadSummary;
  concise?: boolean;
}

function MessageThreadItem({ messageThreadSummary, concise }: Props) {
  const setPopoverOpen = useMessageStore((state) => state.setPopoverOpen);
  const { isApiLoading, readMessageThread } = useMessageThreadItem();
  const timeSince = useTimeSince(messageThreadSummary.latestMessage, 'createdAt', true);

  const isTaskMessage = !!messageThreadSummary.careJourneyInstanceId;
  const isReferralMessage = !!messageThreadSummary.referralId && !!messageThreadSummary.unitId;
  const unreadMessages = messageThreadSummary.unread ?? 0;
  const content = generateText(messageThreadSummary.latestMessage.content, extensions);
  const groupName = messageThreadSummary.units?.[0] ?? messageThreadSummary.roster?.name;
  const groupAbbr = groupName
    ? groupName.includes(' ')
      ? groupName.split(' ').map((w) => w[0].toUpperCase()).join('')
      : groupName
    : null;
  const badgeType = isTaskMessage ? 'Task' : isReferralMessage ? 'Referral' : 'Client';
  const badgeLabel = groupAbbr ? `${badgeType} | ${groupAbbr}` : badgeType;

  const bgColor = isTaskMessage ? colors.purple1 : isReferralMessage ? colors.teal1 : colors.gray2;

  const effectiveRosterId = messageThreadSummary.roster?.id;

  const taskSearchParams = `${SearchParam.TASK_ID}=${messageThreadSummary.careJourneyInstanceId}` +
    (effectiveRosterId
      ? `&${SearchParam.ROSTER_ID}=${effectiveRosterId}`
      : '');

  const referralSearchParams = `${SearchParam.PATIENT_ID}=${messageThreadSummary.clientId}&` +
    `${SearchParam.REFERRAL_ID}=${messageThreadSummary.referralId}&` +
    `${SearchParam.UNIT_ID}=${messageThreadSummary.unitId}`;

  const clientRosterParam = effectiveRosterId
    ? `&${SearchParam.ROSTER_ID}=${effectiveRosterId}`
    : '';
  const clientSearchParams = `${SearchParam.PATIENT_ID}=${messageThreadSummary.clientId}&` +
    `${SearchParam.MESSAGE_THREAD_ID}=${messageThreadSummary.id}${clientRosterParam}`;

  const url = isTaskMessage
    ? `/tasks/details?${taskSearchParams}`
    : isReferralMessage
      ? `/referrals?${referralSearchParams}`
      : `/clients/profile/messages?${clientSearchParams}`;

  const badge = (
    <ToolTip text={groupName ? `Group: ${groupName}` : undefined} type="dark" position="bottom-start">
      <StyledBadge backgroundColor={bgColor} textColor={colors.charcoal}>
        {badgeLabel}
      </StyledBadge>
    </ToolTip>
  );

  return (
    <Wrapper data-widget-item>
      <Main to={url} onClick={() => setPopoverOpen(false)}>
        <Title>
          {badge}
          {messageThreadSummary.about && (
            <About>About: <strong>{messageThreadSummary.about}</strong></About>
          )}
          <TimeSince>{timeSince}</TimeSince>
          {unreadMessages > 1 && (
            <Badge backgroundColor={colors.gray1} textColor={colors.charcoal}>
              {unreadMessages} new
            </Badge>
          )}
        </Title>
        {messageThreadSummary.subject && (
          <SmallTypography>
            S: {messageThreadSummary.subject}
          </SmallTypography>
        )}
        <Message unread={unreadMessages > 0}>
          <Content dangerouslySetInnerHTML={{ __html: content }} />
        </Message>
      </Main>
      <Viewed>
        <WidgetElementViewed
          viewed={!messageThreadSummary.unread}
          disabled={isApiLoading}
          concise={concise}
          onClick={() => readMessageThread(messageThreadSummary, !!messageThreadSummary.unread)}
        />
      </Viewed>
    </Wrapper>
  );
}

export default memo(MessageThreadItem, propsAreEqual);
